import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import api from '../api/api';
import { PageHeader } from '../components/layout';
import {
  Alert,
  ConditionGroupsEditor,
  ErrorState,
  SearchableSelect
} from '../components';
import {
  RuleActionList,
  RuleActionModal,
  RuleConditionModal,
  RuleEditorSummary
} from '../components/rules';
import { getApiErrorMessage } from '../utils/apiError';
import {
  changeConditionOperator,
  createEmptyCondition,
  createEmptyConditionGroup,
  getAiConditionFields,
  getAiCommentConditionOptions,
  validateAiCommentReferences,
  isConditionComplete as isRuleConditionComplete,
  normalizeConditionGroupsForEditing,
  normalizeConditionForEditing,
  sanitizeCondition,
  sanitizeConditionGroups,
  validateConditionGroups
} from '../utils/ruleConditions';
import {
  AI_INPUT_ALL_FIELDS,
  getAiActionSourceFields,
  isAiActionComplete,
  normalizeActionForEdit as normalizeDefaultActionForEdit,
  sanitizeAiAction
} from '../utils/ruleActions';
import {
  getConfiguredStatusName
} from '../utils/statusOptions';

const RuleEditorSkeleton = () => (
  <div
    className="rule-editor-skeleton"
    role="status"
    aria-label="正在加载规则编辑器"
  >
    <span className="rule-editor-skeleton-command" />
    <div className="rule-editor-skeleton-workspace">
      <div>
        <span className="rule-editor-skeleton-section" />
        <span className="rule-editor-skeleton-section is-large" />
        <span className="rule-editor-skeleton-section is-large" />
      </div>
      <span className="rule-editor-skeleton-summary" />
    </div>
  </div>
);

const NewRulePage = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const initialGroupId = searchParams.get('group_id');
  const bodyTextareaRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [rule, setRule] = useState({
    name: '',
    group_id: initialGroupId ? Number(initialGroupId) : null,
    is_active: true,
    match_field: '',
    match_operator: 'contains',
    match_value: '',
    condition_logic: 'and',
    conditions: [createEmptyConditionGroup()],
    action_type: 'assign',
    action_target: '',
    action_params: '',
    priority: 0
  });
  const [groups, setGroups] = useState([]);
  const [persons, setPersons] = useState([]);
  const [fields, setFields] = useState([]);
  const [aiConfigs, setAiConfigs] = useState([]);
  const [aiConfigsError, setAiConfigsError] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [newCondition, setNewCondition] = useState(createEmptyCondition());
  const [newAction, setNewAction] = useState({
    type: 'assign',
    target: [], // 改为数组存储多个人员
    persons: [],
    assign_rule: 'sequence',
    comment_type: 'custom', // 添加评论类型
    comment: '', // 添加评论内容
    ai_prompt: '', // 添加AI提示词
    ai_provider: '', // 添加AI提供商
    method: 'POST',
    url: '',
    headers: [{ name: '', value: '' }],
    body: ''
  });
  const [actions, setActions] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [editingConditionLocation, setEditingConditionLocation] = useState(null);
  const [targetConditionGroupId, setTargetConditionGroupId] = useState(null);
  const [editingActionIndex, setEditingActionIndex] = useState(null);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);

  // 弹窗状态
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  // 操作符映射
  const operatorLabels = {
    contains: '包含',
    not_contains: '不包含',
    equals: '等于',
    not_equals: '不等于',
    regex: '正则匹配',
    is_empty: '为空',
    is_not_empty: '不为空',
    ai_judge: 'AI 判断',
    date_gt: '日期大于',
    date_lt: '日期小于',
    date_gt_days: '大于当前时间天数',
    date_lt_days: '小于当前时间天数',
    date_past_within: '过去N天内',
    date_future_within: '未来N天内'
  };

  const actionLabels = {
    assign: '分配工单',
    update_priority: '更新优先级',
    transition: '状态转换',
    set_field: '设置字段值',
    salesforce_customer_sync: 'Salesforce 客户信息回填',
    ai_set_field: 'AI 生成并设置字段',
    send_https_request: '发送 HTTPS 请求',
    add_comment: '添加评论',
    noop: '不执行任何操作'
  };

  useEffect(() => {
    let active = true;
    const setIfActive = (setter, value) => {
      if (active) setter(value);
    };
    const getCollection = (data, property) => (
      Array.isArray(data) ? data : (Array.isArray(data?.[property]) ? data[property] : [])
    );
    const fetchRule = async () => {
      const response = await api.getRule(editId);
      const ruleData = response.data;
      let parsedConditions = [];

      if (ruleData.conditions) {
        try {
          const parsed = JSON.parse(ruleData.conditions);
          parsedConditions = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('解析条件失败:', error);
        }
      }

      const normalized = normalizeConditionGroupsForEditing(
        parsedConditions,
        ruleData.condition_logic || 'and'
      );
      setIfActive(setRule, {
        ...ruleData,
        condition_logic: normalized.conditionLogic,
        conditions: normalized.groups
      });

      if (!ruleData.action_params) return;
      try {
        const params = JSON.parse(ruleData.action_params);
        if (!Array.isArray(params.actions)) return;
        setIfActive(setActions, params.actions.map(action => (
          action.type === 'assign'
            ? {
              ...action,
              target: action.target
                ? (Array.isArray(action.target) ? action.target : action.target.split(','))
                : [],
              persons: action.persons
                ? (Array.isArray(action.persons) ? action.persons : action.persons.split(','))
                : []
            }
            : action
        )));
      } catch (error) {
        console.error('解析动作参数失败:', error);
      }
    };
    const fetchGroups = async () => {
      const response = await api.getGroups();
      setIfActive(setGroups, getCollection(response.data, 'groups'));
    };
    const fetchPersons = async () => {
      const response = await api.getPersons();
      setIfActive(setPersons, getCollection(response.data, 'persons'));
    };
    const fetchFields = async () => {
      const response = await api.getFields();
      setIfActive(setFields, getCollection(response.data, 'fields'));
    };
    const fetchAIConfigs = async () => {
      try {
        const response = await api.getRuleAIChannels();
        setIfActive(setAiConfigs, Array.isArray(response.data) ? response.data : []);
        setIfActive(setAiConfigsError, '');
      } catch (error) {
        if (error.response?.status !== 403) throw error;
        setIfActive(setAiConfigs, []);
        setIfActive(setAiConfigsError, '需要规则维护权限才能选择 AI 渠道');
      }
    };
    const fetchStatuses = async () => {
      const response = await api.getStatuses();
      setIfActive(setStatuses, Array.isArray(response.data) ? response.data : []);
    };
    const loadEditor = async () => {
      setIsPageLoading(true);
      setLoadError('');
      setIsEditing(Boolean(editId));
      try {
        await Promise.all([
          fetchGroups(),
          fetchPersons(),
          fetchFields(),
          fetchAIConfigs(),
          fetchStatuses(),
          editId ? fetchRule() : Promise.resolve()
        ]);
      } catch (error) {
        if (active) {
          setLoadError(getApiErrorMessage(error, '规则编辑器加载失败'));
        }
      } finally {
        if (active) setIsPageLoading(false);
      }
    };

    loadEditor();
    return () => {
      active = false;
    };
  }, [editId, loadAttempt]);

  const selectedConditionField = useMemo(
    () => fields.find(field => field.field_id === newCondition.field),
    [fields, newCondition.field]
  );

  const conditionFieldOptions = useMemo(
    () => Array.isArray(selectedConditionField?.options) ? selectedConditionField.options : [],
    [selectedConditionField]
  );

  const conditionCount = useMemo(
    () => rule.conditions.reduce(
      (total, group) => total + (group.conditions?.length || 0),
      0
    ),
    [rule.conditions]
  );

  const selectedActionField = useMemo(
    () => fields.find(field => field.field_id === newAction.field),
    [fields, newAction.field]
  );

  const actionFieldOptions = useMemo(
    () => Array.isArray(selectedActionField?.options) ? selectedActionField.options : [],
    [selectedActionField]
  );

  const selectedGroupName = useMemo(
    () => groups.find(group => String(group.id) === String(rule.group_id))?.name || '请选择规则组',
    [groups, rule.group_id]
  );

  const filteredPersons = useMemo(() => {
    const keyword = assigneeSearch.trim().toLowerCase();
    if (!keyword) return persons;
    return persons.filter(person => [
      person.name,
      person.display_name,
      person.email,
      person.account_id
    ].some(value => String(value || '').toLowerCase().includes(keyword)));
  }, [persons, assigneeSearch]);

  const selectedAssigneeIds = useMemo(
    () => Array.isArray(newAction.target) ? newAction.target : [],
    [newAction.target]
  );

  const selectedAssignees = useMemo(
    () => selectedAssigneeIds.map(id => (
      persons.find(person => person.account_id === id) || {
        account_id: id,
        name: id.substring(0, 10),
        email: id
      }
    )),
    [selectedAssigneeIds, persons]
  );

  const fieldInsertOptions = useMemo(
    () => [
      { value: 'issue.key', label: '工单 Key' },
      ...fields.map(field => ({ value: field.field_id, label: field.display_name || field.field_id }))
    ],
    [fields]
  );

  const aiInputFieldOptions = useMemo(
    () => [
      { value: AI_INPUT_ALL_FIELDS, label: '全部字段' },
      ...fields.map(field => ({ value: field.field_id, label: field.display_name || field.field_id }))
    ],
    [fields]
  );

  const createDefaultAction = (type = 'assign') => ({
    type,
    target: type === 'assign' ? [] : '',
    persons: type === 'assign' ? [] : '',
    assign_rule: 'sequence',
    comment_type: 'custom',
    comment: '',
    ai_prompt: '',
    ai_provider: '',
    source_fields: [AI_INPUT_ALL_FIELDS],
    field: '',
    value: '',
    handle_field: '',
    target_fields: {
      customer_names: '',
      stock_monthly_gmv: '',
      potential_monthly_gmv: '',
      total_monthly_gmv: '',
      customer_new_year: ''
    },
    split_delimiters: [',', '，', ';', '；', '\n'],
    clear_on_no_match: true,
    method: 'POST',
    url: '',
    headers: [{ name: '', value: '' }],
    body: ''
  });

  const insertTextAtCursor = (value, insertText, inputElement) => {
    const source = value || '';
    if (!inputElement || typeof inputElement.selectionStart !== 'number') {
      return `${source}${insertText}`;
    }
    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;
    return `${source.slice(0, start)}${insertText}${source.slice(end)}`;
  };

  const insertBodyFieldPlaceholder = (fieldId) => {
    if (!fieldId) return;
    const placeholder = `{{${fieldId}}}`;
    setNewAction(prev => ({
      ...prev,
      body: insertTextAtCursor(prev.body, placeholder, bodyTextareaRef.current)
    }));
  };

  const updateHeaderRow = (index, key, value) => {
    setNewAction(prev => ({
      ...prev,
      headers: (prev.headers || [{ name: '', value: '' }]).map((header, headerIndex) => (
        headerIndex === index ? { ...header, [key]: value } : header
      ))
    }));
  };

  const addHeaderRow = () => {
    setNewAction(prev => ({
      ...prev,
      headers: [...(prev.headers || []), { name: '', value: '' }]
    }));
  };

  const removeHeaderRow = (index) => {
    setNewAction(prev => ({
      ...prev,
      headers: (prev.headers || []).filter((_, headerIndex) => headerIndex !== index)
    }));
  };

  const validateHttpsBodyJson = (body) => {
    const trimmedBody = String(body || '').trim();
    if (!trimmedBody) return '';
    try {
      JSON.parse(trimmedBody);
      return '';
    } catch (error) {
      return error.message || 'Body 不是有效的 JSON';
    }
  };

  const bodyJsonError = useMemo(
    () => newAction.type === 'send_https_request' ? validateHttpsBodyJson(newAction.body) : '',
    [newAction.type, newAction.body]
  );

  const formatHttpsBodyJson = () => {
    const trimmedBody = String(newAction.body || '').trim();
    if (!trimmedBody || bodyJsonError) return;
    const formattedBody = JSON.stringify(JSON.parse(trimmedBody), null, 2);
    setNewAction(prev => ({ ...prev, body: formattedBody }));
  };

  const aiConditionOptions = getAiCommentConditionOptions(rule.conditions);
  const isConditionResultComplete = newAction.comment_type === 'condition_result'
    && aiConditionOptions.some(option => option.value === newAction.ai_condition_id);
  const isConditionComplete = isRuleConditionComplete(newCondition);

  const isActionComplete = Boolean(
    (newAction.type === 'assign' && newAction.target.length > 0) ||
    (newAction.type === 'update_priority' && newAction.target) ||
    (newAction.type === 'transition' && newAction.target) ||
    (newAction.type === 'set_field' && newAction.field && newAction.value) ||
    (newAction.type === 'salesforce_customer_sync' &&
      newAction.handle_field &&
      newAction.target_fields?.customer_names) ||
    (newAction.type === 'ai_set_field' && isAiActionComplete(newAction)) ||
    (newAction.type === 'send_https_request' && newAction.method && newAction.url && newAction.url.startsWith('https://') && !bodyJsonError) ||
    (newAction.type === 'add_comment' &&
      (
        (newAction.comment_type === 'custom' && newAction.comment) ||
        (newAction.comment_type === 'ai' && isAiActionComplete(newAction)) || isConditionResultComplete
      )) ||
    newAction.type === 'noop'
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRule(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'group_id' && value ? parseInt(value, 10) : value)
    }));
  };

  const handleConditionFieldChange = (value) => {
    setNewCondition(prev => {
      if (prev.operator === 'ai_judge') {
        return {
          ...prev,
          field: '',
          fields: Array.isArray(value) ? value : []
        };
      }
      return {
        ...prev,
        field: value,
        fields: [],
        value: ''
      };
    });
  };

  const addCondition = () => {
    if (!isRuleConditionComplete(newCondition)) return;
    const conditionToSave = sanitizeCondition(newCondition);
    const newEditorCondition = normalizeConditionGroupsForEditing(
      [conditionToSave],
      'and'
    ).groups[0].conditions[0];
    
    setRule(prev => {
      const nextGroups = prev.conditions.map(group => {
        if (
          editingConditionLocation
          && group._editorId === editingConditionLocation.groupId
        ) {
          return {
            ...group,
            conditions: group.conditions.map(condition => (
              condition._editorId === editingConditionLocation.conditionId
                ? {
                    ...normalizeConditionForEditing(conditionToSave),
                    _editorId: condition._editorId
                  }
                : condition
            ))
          };
        }
        if (
          !editingConditionLocation
          && group._editorId === targetConditionGroupId
        ) {
          return {
            ...group,
            conditions: [...group.conditions, newEditorCondition]
          };
        }
        return group;
      });
      return {
        ...prev,
        conditions: nextGroups
      };
    });
    setNewCondition(createEmptyCondition());
    setEditingConditionLocation(null);
    setTargetConditionGroupId(null);
    setShowConditionModal(false);
  };

  const editCondition = (groupId, conditionId) => {
    const group = rule.conditions.find(item => item._editorId === groupId);
    const condition = group?.conditions.find(
      item => item._editorId === conditionId
    );
    if (!condition) return;
    setNewCondition(normalizeConditionForEditing(condition));
    setEditingConditionLocation({ groupId, conditionId });
    setTargetConditionGroupId(groupId);
    setShowConditionModal(true);
  };

  const cancelConditionEdit = () => {
    setNewCondition(createEmptyCondition());
    setEditingConditionLocation(null);
    setTargetConditionGroupId(null);
    setShowConditionModal(false);
  };

  const openConditionModal = (groupId) => {
    setNewCondition(createEmptyCondition());
    setEditingConditionLocation(null);
    setTargetConditionGroupId(groupId);
    setShowConditionModal(true);
  };

  const addAction = () => {
    const saveAction = (actionToSave, resetType = 'assign') => {
      setActions(prev => {
        if (editingActionIndex !== null) {
          return prev.map((action, index) => index === editingActionIndex ? actionToSave : action);
        }
        return [...prev, actionToSave];
      });
      setEditingActionIndex(null);
      setNewAction(createDefaultAction(resetType));
      setShowActionModal(false);
    };

    if (newAction.type === 'assign') {
      if (newAction.target.length > 0) {
        // 存储时转换为逗号分隔的字符串
        const actionToSave = {
          ...newAction,
          target: newAction.target.join(','),
          persons: newAction.persons.join(',')
        };
        saveAction(actionToSave, 'assign');
      }
    } else if (newAction.type === 'add_comment') {
      // 评论动作需要comment或ai配置
      if ((newAction.comment_type === 'custom' && newAction.comment) || 
          (newAction.comment_type === 'ai' && isAiActionComplete(newAction)) || isConditionResultComplete) {
        const normalizedAction = {
          ...newAction,
          // 确保target和persons是逗号分隔字符串（尽管对于评论动作它们通常为空数组）
          target: Array.isArray(newAction.target) ? newAction.target.join(',') : newAction.target,
          persons: Array.isArray(newAction.persons) ? newAction.persons.join(',') : newAction.persons
        };
        const actionToSave = newAction.comment_type === 'ai'
          ? sanitizeAiAction(normalizedAction)
          : normalizedAction;
        saveAction(actionToSave, 'add_comment');
      }
    } else if (newAction.type === 'send_https_request') {
      if (newAction.url && newAction.url.startsWith('https://')) {
        const jsonError = validateHttpsBodyJson(newAction.body);
        if (jsonError) {
          setAlertState({ isOpen: true, message: `Body JSON 格式错误: ${jsonError}` });
          return;
        }
        const actionToSave = {
          type: 'send_https_request',
          method: newAction.method || 'POST',
          url: newAction.url,
          headers: (newAction.headers || [])
            .filter(header => (header.name || '').trim())
            .map(header => ({ name: header.name.trim(), value: header.value || '' })),
          body: newAction.body || ''
        };
        saveAction(actionToSave, 'send_https_request');
      }
    } else if (newAction.type === 'noop') {
      // 不执行任何操作 - 直接添加，不需要额外参数
      const actionToSave = {
        type: 'noop',
        target: '',
        persons: ''
      };
      saveAction(actionToSave, 'assign');
    } else if (newAction.type === 'set_field') {
      // 设置字段值
      if (newAction.field && newAction.value) {
        const actionToSave = { ...newAction };
        saveAction(actionToSave, 'assign');
      }
    } else if (newAction.type === 'salesforce_customer_sync') {
      if (
        newAction.handle_field
        && newAction.target_fields?.customer_names
      ) {
        saveAction({
          type: 'salesforce_customer_sync',
          handle_field: newAction.handle_field,
          target_fields: newAction.target_fields,
          split_delimiters: newAction.split_delimiters,
          clear_on_no_match: newAction.clear_on_no_match !== false,
          target: '',
          persons: ''
        }, 'assign');
      }
    } else if (newAction.type === 'ai_set_field') {
      if (isAiActionComplete(newAction)) {
        const actionToSave = sanitizeAiAction({
          type: 'ai_set_field',
          source_fields: newAction.source_fields,
          ai_provider: newAction.ai_provider,
          ai_prompt: newAction.ai_prompt,
          field: newAction.field,
          target: '',
          persons: ''
        });
        saveAction(actionToSave, 'assign');
      }
    } else if (newAction.type) {
      // 其他动作类型
      const actionToSave = { 
        ...newAction,
        target: Array.isArray(newAction.target) ? newAction.target.join(',') : newAction.target,
        persons: Array.isArray(newAction.persons) ? newAction.persons.join(',') : newAction.persons
      };
      saveAction(actionToSave, 'assign');
    }
  };

  const removeAction = (index) => {
    setActions(prev => prev.filter((_, i) => i !== index));
    if (editingActionIndex === index) {
      cancelActionEdit();
    } else if (editingActionIndex !== null && index < editingActionIndex) {
      setEditingActionIndex(prev => prev - 1);
    }
  };

  const normalizeActionForEdit = (action) => {
    if (action.type === 'assign') {
      const target = action.target ? (Array.isArray(action.target) ? action.target : action.target.split(',').filter(Boolean)) : [];
      const persons = action.persons ? (Array.isArray(action.persons) ? action.persons : action.persons.split(',').filter(Boolean)) : target;
      return { ...createDefaultAction('assign'), ...action, target, persons };
    }
    if (action.type === 'send_https_request') {
      return {
        ...createDefaultAction('send_https_request'),
        ...action,
        headers: Array.isArray(action.headers) && action.headers.length > 0 ? action.headers : [{ name: '', value: '' }]
      };
    }
    return normalizeDefaultActionForEdit(
      action,
      createDefaultAction(action.type || 'assign')
    );
  };

  const editAction = (index) => {
    const action = actions[index];
    if (!action) return;
    setNewAction(normalizeActionForEdit(action));
    setEditingActionIndex(index);
    setShowActionModal(true);
  };

  const cancelActionEdit = () => {
    setNewAction(createDefaultAction('assign'));
    setEditingActionIndex(null);
    setShowActionModal(false);
  };

  const openActionModal = () => {
    setNewAction(createDefaultAction('assign'));
    setEditingActionIndex(null);
    setAssigneeSearch('');
    setShowActionModal(true);
  };

  const toggleAssignee = (accountId, isChecked) => {
    setNewAction(prev => ({
      ...prev,
      target: isChecked
        ? [...prev.target, accountId]
        : prev.target.filter(id => id !== accountId),
      persons: isChecked
        ? [...prev.persons, accountId]
        : prev.persons.filter(id => id !== accountId)
    }));
  };

  const clearAssignees = () => {
    setNewAction(prev => ({ ...prev, target: [], persons: [] }));
  };

  const validateRuleBeforeSave = () => {
    if (!rule.name.trim()) {
      return '请输入规则名称';
    }
    if (!rule.group_id) {
      return '请选择规则组';
    }
    const conditionValidationMessage = validateConditionGroups(rule.conditions);
    if (conditionValidationMessage) return conditionValidationMessage;
    const referenceError = validateAiCommentReferences(rule.conditions, actions);
    if (referenceError) return referenceError;
    if (!Array.isArray(actions) || actions.length === 0) {
      return '请至少添加一个执行动作';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    const validationMessage = validateRuleBeforeSave();
    if (validationMessage) {
      setAlertState({ isOpen: true, message: validationMessage });
      return;
    }

    setIsSaving(true);
    try {
      // 处理动作，确保target和persons是字符串格式
      const processedActions = actions.map(action => {
        if (action.type === 'assign') {
          // 对于assign动作，将数组转换为逗号分隔的字符串
          return {
            ...action,
            target: Array.isArray(action.target) ? action.target.join(',') : action.target,
            persons: Array.isArray(action.persons) ? action.persons.join(',') : action.persons
          };
        } else {
          // 对于其他类型的动作，确保不包含数组格式的target
          return {
            ...action,
            target: Array.isArray(action.target) ? action.target.join(',') : action.target,
            persons: Array.isArray(action.persons) ? action.persons.join(',') : action.persons
          };
        }
      });
      
      const ruleData = {
        ...rule,
        conditions: sanitizeConditionGroups(rule.conditions),
        action_params: JSON.stringify({ actions: processedActions })
      };
      
      if (isEditing) {
        await api.updateRule(editId, ruleData);
      } else {
        await api.createRule(ruleData);
      }
      
      navigate('/rules');
    } catch (err) {
      setAlertState({
        isOpen: true,
        message: '保存规则失败: ' + getApiErrorMessage(err, '未知错误')
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 获取人员名称显示
  const getPersonNames = (targetIds) => {
    if (!targetIds) return '-';
    const ids = Array.isArray(targetIds) ? targetIds : targetIds.split(',');
    const names = ids.map(id => {
      const person = persons.find(p => p.account_id === id);
      return person ? person.name : id.substring(0, 15) + '...';
    });
    return names.join(', ');
  };

  const getFieldDisplayName = (fieldId) => {
    const field = fields.find(item => item.field_id === fieldId);
    return field?.display_name || fieldId;
  };

  const getConditionSummary = (condition) => {
    if (!condition) return { title: '-', detail: '-' };
    const operatorName = operatorLabels[condition.operator] || condition.operator;
    if (condition.operator === 'ai_judge') {
      const fieldName = getAiConditionFields(condition).map(getFieldDisplayName).join(', ');
      return {
        title: fieldName || '未选择字段',
        detail: `${condition.ai_mode === 'judge_and_comment' ? '判断并生成评论' : operatorName} · ${condition.ai_provider || '未选择 AI'}`,
        meta: condition.value || '未填写提示词'
      };
    }
    const fieldName = getFieldDisplayName(condition.field);
    if (['is_empty', 'is_not_empty'].includes(condition.operator)) {
      return { title: fieldName, detail: operatorName, meta: '无需条件值' };
    }
    return {
      title: fieldName,
      detail: operatorName,
      meta: condition.value || '未填写条件值'
    };
  };

  const getActionSummary = (action) => {
    const label = actionLabels[action.type] || action.type || '未知动作';
    const getAiSourceFieldNames = (aiAction) => (
      getAiActionSourceFields(aiAction)
        .map(fieldId => (
          fieldId === AI_INPUT_ALL_FIELDS
            ? '全部字段'
            : getFieldDisplayName(fieldId)
        ))
        .join('、')
    );
    if (action.type === 'assign') {
      return {
        title: label,
        detail: getPersonNames(action.target),
        meta: action.assign_rule === 'first' ? '分配给第一个人' : action.assign_rule === 'random' ? '随机分配' : '顺序分配'
      };
    }
    if (action.type === 'add_comment') {
      if (action.comment_type === 'condition_result') {
        return { title: label, detail: '使用 AI 条件结果',
          meta: aiConditionOptions.find(option => option.value === action.ai_condition_id)?.label || '引用条件已失效，请重新选择' };
      }
      return {
        title: label,
        detail: action.comment_type === 'ai' ? 'AI 生成评论' : '自定义评论',
        meta: action.comment_type === 'ai'
          ? `${getAiSourceFieldNames(action)} · ${action.ai_provider || '未选择 AI'} · ${action.ai_prompt || '未填写 AI 提示词'}`
          : (action.comment || '未填写评论内容')
      };
    }
    if (action.type === 'transition') {
      return {
        title: label,
        detail: getConfiguredStatusName(statuses, action.target) || '未选择状态',
        meta: 'Jira 状态转换'
      };
    }
    if (action.type === 'set_field') {
      return {
        title: label,
        detail: getFieldDisplayName(action.field),
        meta: action.value || '未填写字段值'
      };
    }
    if (action.type === 'salesforce_customer_sync') {
      const targetKeys = [
        'customer_names',
        'stock_monthly_gmv',
        'potential_monthly_gmv',
        'total_monthly_gmv',
        'customer_new_year'
      ];
      return {
        title: label,
        detail: getFieldDisplayName(action.handle_field) || '未选择 Handle 字段',
        meta: action.target_fields?.customer_names
          ? `已配置 ${targetKeys.filter(key => action.target_fields?.[key]).length} 个回填字段`
          : '回填字段未配置完整'
      };
    }
    if (action.type === 'ai_set_field') {
      return {
        title: label,
        detail: getFieldDisplayName(action.field),
        meta: `${getAiSourceFieldNames(action)} · ${action.ai_provider || '未选择 AI'} · ${action.ai_prompt || '未填写 AI 提示词'}`
      };
    }
    if (action.type === 'send_https_request') {
      return {
        title: label,
        detail: `${(action.method || 'POST').toUpperCase()} ${action.url || '未填写 URL'}`,
        meta: `${(action.headers || []).filter(header => header.name).length} 个 Header · ${action.body ? '有 Body' : '无 Body'}`
      };
    }
    if (action.type === 'noop') {
      return { title: label, detail: '不执行任何操作', meta: '仅记录匹配结果' };
    }
    return { title: label, detail: action.target || '-', meta: '' };
  };

  if (isPageLoading) {
    return (
      <div className="rule-editor-page">
        <RuleEditorSkeleton />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rule-editor-page">
        <ErrorState
          title="规则编辑器加载失败"
          description={loadError}
          retryLabel="重新加载"
          onRetry={() => setLoadAttempt(attempt => attempt + 1)}
        />
      </div>
    );
  }

  return (
    <div className="rule-editor-page">
      <div className="rule-editor-command-bar">
        <PageHeader title={isEditing ? '编辑规则' : '新建规则'} actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/rules')}>取消</button>
            <button type="submit" form="rule-editor-form" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? '保存中' : '保存规则'}
            </button>
          </>
        } />
      </div>

      <form id="rule-editor-form" onSubmit={handleSubmit} className="rule-editor-workspace">
        <main className="rule-editor-canvas">
        <section className="rule-editor-section rule-editor-stage-overview">
          <header className="rule-editor-section-header">
            <h2>基本信息</h2>
            <span className={`badge ${rule.is_active ? 'badge-success' : 'badge-default'}`}>
              {rule.is_active ? '启用中' : '已禁用'}
            </span>
          </header>
          <div className="rule-editor-section-body">
            <div className="rule-editor-basic-grid">
              <div className="form-group">
                <label className="form-label">规则名称 *</label>
                <input
                  type="text"
                  name="name"
                  value={rule.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="输入规则名称"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">规则组 *</label>
                <SearchableSelect
                  value={rule.group_id || ''}
                  options={groups.map(group => ({ value: group.id, label: group.name }))}
                  onChange={(val) => handleInputChange({ target: { name: 'group_id', value: val } })}
                  placeholder="选择规则组"
                />
              </div>
              <div className="form-group">
                <label className="form-label">优先级</label>
                <input
                  type="number"
                  name="priority"
                  value={rule.priority}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  placeholder="数值越小优先级越高"
                />
              </div>
            </div>

            <div className="form-group rule-editor-active-control">
              <label className="form-label">启用状态</label>
              <div className="rule-editor-active-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={rule.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="switch-slider"></span>
                </label>
                <span>
                  {rule.is_active ? '启用' : '禁用'}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="rule-editor-section rule-editor-stage-conditions">
          <header className="rule-editor-section-header">
            <h2>匹配条件</h2>
            <span className="badge badge-default">{conditionCount} 个条件</span>
          </header>
          <div className="rule-editor-section-body">
            <ConditionGroupsEditor
              groups={rule.conditions}
              betweenLogic={rule.condition_logic}
              onBetweenLogicChange={(value) => (
                setRule(prev => ({ ...prev, condition_logic: value }))
              )}
              onGroupsChange={(conditionGroups) => (
                setRule(prev => ({ ...prev, conditions: conditionGroups }))
              )}
              getConditionSummary={getConditionSummary}
              onAddCondition={openConditionModal}
              onEditCondition={editCondition}
            />

            <RuleConditionModal
              isOpen={showConditionModal}
              isEditing={Boolean(editingConditionLocation)}
              condition={newCondition}
              fields={fields}
              aiConfigs={aiConfigs}
              aiConfigsError={aiConfigsError}
              persons={persons}
              conditionFieldOptions={conditionFieldOptions}
              isComplete={isConditionComplete}
              onChange={setNewCondition}
              onOperatorChange={(operator) => (
                setNewCondition(previous => (
                  changeConditionOperator(previous, operator)
                ))
              )}
              onFieldChange={handleConditionFieldChange}
              onSubmit={addCondition}
              onClose={cancelConditionEdit}
            />
          </div>
        </section>

        <section className="rule-editor-section rule-editor-stage-actions">
          <header className="rule-editor-section-header">
            <div className="rule-editor-section-title">
              <h2>动作配置</h2>
              <span className="badge badge-default">{actions.length} 个动作</span>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm rule-editor-stage-action-add"
              onClick={openActionModal}
            >
              <Plus size={15} aria-hidden="true" />
              添加执行动作
            </button>
          </header>
          <div className="rule-editor-section-body">
            {actions.length > 0 && (
              <RuleActionList
                actions={actions}
                getSummary={getActionSummary}
                editingIndex={editingActionIndex}
                onEdit={editAction}
                onDelete={removeAction}
              />
            )}

            <RuleActionModal
              isOpen={showActionModal}
              isEditing={editingActionIndex !== null}
              action={newAction}
              aiConditionOptions={aiConditionOptions}
              fields={fields}
              aiConfigs={aiConfigs}
              aiConfigsError={aiConfigsError}
              statuses={statuses}
              actionFieldOptions={actionFieldOptions}
              aiInputFieldOptions={aiInputFieldOptions}
              fieldInsertOptions={fieldInsertOptions}
              selectedAssigneeIds={selectedAssigneeIds}
              selectedAssignees={selectedAssignees}
              filteredPersons={filteredPersons}
              assigneeSearch={assigneeSearch}
              bodyJsonError={bodyJsonError}
              bodyTextareaRef={bodyTextareaRef}
              isComplete={isActionComplete}
              onChange={setNewAction}
              onSearchChange={setAssigneeSearch}
              onTypeChange={(type) => setNewAction(createDefaultAction(type))}
              onToggleAssignee={toggleAssignee}
              onClearAssignees={clearAssignees}
              onAddHeader={addHeaderRow}
              onUpdateHeader={updateHeaderRow}
              onRemoveHeader={removeHeaderRow}
              onFormatBody={formatHttpsBodyJson}
              onInsertBodyField={insertBodyFieldPlaceholder}
              onSubmit={addAction}
              onClose={cancelActionEdit}
            />

          </div>
        </section>

        </main>

        <aside className="rule-editor-summary-column">
          <RuleEditorSummary
            groupName={selectedGroupName}
            isActive={rule.is_active}
            conditionCount={conditionCount}
            actionCount={actions.length}
            priority={rule.priority}
          />
        </aside>
      </form>

      <Alert
        isOpen={alertState.isOpen}
        title="提示"
        message={alertState.message}
        onClose={() => setAlertState({ ...alertState, isOpen: false })}
      />
    </div>
  );
};

export default NewRulePage;
